import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, ArrowRight, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CsvColumnMapper — Round 84h (Feb 14, 2026).
 *
 * Opens from inside `CsvImportModal` when the user clicks
 * "Auto-detect failed? Map columns manually". Calls
 * `/api/portfolio/csv-headers` to read the CSV column names + first
 * 5 sample rows, lets the user dropdown-pick which column maps to
 * each system field (Ticker / Shares / Cost / Date / Type), then
 * calls `/api/portfolio/csv-preview-mapped` and hands the resulting
 * preview back to the parent via `onPreview`.
 *
 * Pre-fills dropdowns from the backend's `suggested_mapping` so the
 * UX feels "smart" — user just confirms or tweaks one or two fields.
 */
const FIELDS = [
  { key: 'ticker', label: 'Ticker / Symbol *', required: true },
  { key: 'shares', label: 'Shares / Quantity *', required: true },
  { key: 'cost',   label: 'Cost / Price per share', required: false },
  { key: 'date',   label: 'Purchase / Trade date', required: false },
  { key: 'type',   label: 'Activity type (Buy / Sell / DRIP)', required: false },
];

export default function CsvColumnMapper({ open, csvText, portfolioId = null, onClose, onPreview }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [samples, setSamples] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [mapping, setMapping] = useState({});

  useEffect(() => {
    if (!open) return undefined;
    if (!csvText) return undefined;
    setLoading(true);
    setHeaders([]); setSamples([]); setMapping({});
    axios
      .post(`${API}/portfolio/csv-headers`, { csv_text: csvText }, { withCredentials: true })
      .then((r) => {
        setHeaders(r.data?.headers || []);
        setSamples(r.data?.samples || []);
        setRowCount(r.data?.row_count || 0);
        setMapping(r.data?.suggested_mapping || {});
      })
      .catch((e) => {
        toast.error('Could not read CSV headers', {
          description: e?.response?.data?.detail || 'Make sure the file is a valid CSV.',
        });
        onClose();
      })
      .finally(() => setLoading(false));
    return undefined;
  }, [open, csvText, onClose]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!mapping.ticker || !mapping.shares) {
      toast.error('Ticker and Shares are required');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/portfolio/csv-preview-mapped`,
        // Round 92o++++++++N++++++++++ — scope dupe-check to target
        // portfolio (Plus multi-portfolio). See CsvImportModal for
        // the full rationale.
        { csv_text: csvText, mapping, portfolio_id: portfolioId || null },
        { withCredentials: true },
      );
      onPreview(data);
      onClose();
    } catch (e) {
      toast.error('Mapping failed', {
        description: e?.response?.data?.detail || 'Please double-check your column choices.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-4"
      data-testid="csv-mapper-overlay"
      // Round 92o++++++++N++++++++++ (May 2026) — Backdrop-only close.
      // User reported the modal "flashes off/on really fast" when
      // clicking the horizontal scrollbar of the preview table at
      // the bottom. Root cause: the previous handler was just
      // `onClick={onClose}`, so any click bubbling up from the
      // scrollable preview reached this backdrop and closed the
      // modal. Inner content already had `e.stopPropagation()`, but
      // certain native scrollbar interactions still reach the
      // backdrop with `e.target === e.currentTarget` patterns that
      // bypass it. Restrict close to *only* clicks whose target IS
      // the backdrop element itself.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="csv-mapper-modal"
      >
        <div className="px-4 py-3 border-b border-border bg-muted/40 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base font-bold">Map your CSV columns</span>
            {rowCount > 0 && (
              <span className="text-[10px] text-muted-foreground font-mono">
                · {rowCount.toLocaleString()} rows detected
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
            data-testid="csv-mapper-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-border bg-violet-50/40 dark:bg-violet-950/20 flex items-start gap-2">
          <Info className="w-4 h-4 text-violet-600 dark:text-violet-400 shrink-0 mt-0.5" />
          <p className="text-[11px] sm:text-xs text-muted-foreground leading-relaxed">
            For each system field, pick the matching column from your CSV. We've taken our best guess based on common header names — confirm or tweak below. Numeric values like <code className="bg-muted px-1 rounded">$1,250.50</code> are cleaned automatically; ticker formats like <code className="bg-muted px-1 rounded">BRK-B</code> / <code className="bg-muted px-1 rounded">AAPL:NASDAQ</code> are normalized.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24 sm:pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Reading headers…
            </div>
          ) : (
            <>
              {FIELDS.map((f) => (
                <div key={f.key} className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-center">
                  <label className="text-sm font-semibold sm:col-span-2" htmlFor={`csv-map-${f.key}`}>
                    {f.label}
                  </label>
                  <div className="sm:col-span-3">
                    <select
                      id={`csv-map-${f.key}`}
                      value={mapping[f.key] || ''}
                      onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value || null })}
                      className="w-full h-10 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                      data-testid={`csv-map-${f.key}-select`}
                    >
                      <option value="">— Skip / not present —</option>
                      {headers.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    {mapping[f.key] && samples.length > 0 ? (
                      <p className="text-[10px] text-muted-foreground mt-1 font-mono truncate">
                        e.g. {samples.slice(0, 3).map((row) => {
                          const idx = headers.indexOf(mapping[f.key]);
                          return idx >= 0 ? row[idx] : '';
                        }).filter(Boolean).join(' · ') || '(empty cells)'}
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}

              {samples.length > 0 ? (
                <div className="mt-4 rounded-lg border bg-muted/20 overflow-x-auto" data-testid="csv-mapper-samples">
                  <p className="text-[11px] font-semibold text-muted-foreground px-3 py-1.5">First {samples.length} rows from your file:</p>
                  <table className="w-full text-[11px]">
                    <thead className="bg-muted/40">
                      <tr>
                        {headers.map((h) => (
                          <th key={h} className="px-2 py-1 text-left font-mono">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {samples.map((r, i) => (
                        <tr key={i} className="border-t border-border/40">
                          {r.map((c, j) => (
                            <td key={j} className="px-2 py-1 font-mono whitespace-nowrap">{c}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-10 px-4 rounded-md border bg-background text-sm font-semibold hover:bg-muted disabled:opacity-60"
            data-testid="csv-mapper-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || loading}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-60"
            data-testid="csv-mapper-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {submitting ? 'Building preview…' : 'Preview my data'}
          </button>
        </div>
      </div>
    </div>
  );
}
